import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Home, Search, MessageSquare, Sparkles, User, CreditCard, KeyRound,
  Wallet, FolderLock, FileText, ChevronUp, Layers, Check
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useApplications, APP_ICON_MAP, APP_COLOR_MAP } from '../../hooks/useApplications';
import MobileSearchExploreModal from './MobileSearchExploreModal';
import MobileChatAssistantModal from './MobileChatAssistantModal';
import MobileProfileSettingsModal from './MobileProfileSettingsModal';

// Default 3 pinned apps fallback
const DEFAULT_PINNED_FALLBACK = [
  {
    id: 'cards',
    slug: 'cards',
    name: 'Cards',
    path: '/apps/cards',
    icon: CreditCard,
    color: '#f59e0b',
    gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
  },
  {
    id: 'passwords',
    slug: 'passwords',
    name: 'Vault',
    path: '/vault',
    icon: KeyRound,
    color: '#3b82f6',
    gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
  },
  {
    id: 'expense-tracker',
    slug: 'expense-tracker',
    name: 'Expense',
    path: '/apps/expense-tracker',
    icon: Wallet,
    color: '#10b981',
    gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
  },
];

export default function MobileBottomNav({ onOpenMobileInstall, isSidebarOpen, isModalOpen, hideDock: hideDockProp }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { pinnedApplications } = useApplications();

  // Modal states
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isQuickMenuOpen, setIsQuickMenuOpen] = useState(false);

  // 3 Pinned Menus in Center Notch
  const threePinnedApps = useMemo(() => {
    if (pinnedApplications && pinnedApplications.length >= 3) {
      return pinnedApplications.slice(0, 3).map(app => {
        const IconComp = APP_ICON_MAP[app.slug] || APP_ICON_MAP[app.icon] || CreditCard;
        const color = app.color || APP_COLOR_MAP[app.slug] || '#f59e0b';
        return {
          id: app.id || app.slug,
          slug: app.slug,
          name: app.name,
          path: app.slug === 'passwords' ? '/vault' : `/apps/${app.slug}`,
          icon: IconComp,
          color,
          gradient: `linear-gradient(135deg, ${color} 0%, ${color}cc 100%)`,
        };
      });
    }
    return DEFAULT_PINNED_FALLBACK;
  }, [pinnedApplications]);

  // Active Pinned App index (0, 1, 2)
  const [activePinIndex, setActivePinIndex] = useState(0);
  const [isPinFlipping, setIsPinFlipping] = useState(false);

  // Auto-switch between the 3 pinned apps every 4 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setIsPinFlipping(true);
      setTimeout(() => {
        setActivePinIndex(prev => (prev + 1) % threePinnedApps.length);
        setIsPinFlipping(false);
      }, 300);
    }, 4000);

    return () => clearInterval(timer);
  }, [threePinnedApps.length]);

  // Manual Switch to Next Pinned App
  const handleSwitchPinned = (e) => {
    e.stopPropagation();
    if (navigator.vibrate) navigator.vibrate(25);
    setIsPinFlipping(true);
    setTimeout(() => {
      setActivePinIndex(prev => (prev + 1) % threePinnedApps.length);
      setIsPinFlipping(false);
    }, 200);
  };

  // Launch Active Pinned App
  const handleLaunchActivePinned = () => {
    if (navigator.vibrate) navigator.vibrate(40);
    const activeApp = threePinnedApps[activePinIndex] || threePinnedApps[0];
    navigate(activeApp.path);
  };

  const currentPinned = threePinnedApps[activePinIndex] || threePinnedApps[0];
  const PinnedIcon = currentPinned.icon;

  // Active route indicators
  const isHomeActive = location.pathname === '/' || location.pathname === '/dashboard';
  const isSearchActive = isSearchOpen;
  const isChatActive = isChatOpen;
  const isProfileActive = isProfileOpen;

  // Dynamic screen width tracking for pixel-perfect edge-to-edge docking
  const [barWidth, setBarWidth] = useState(
    () => (typeof window !== 'undefined' ? (window.visualViewport?.width || window.innerWidth || document.documentElement.clientWidth || 375) : 375)
  );
  const containerRef = useRef(null);

  useEffect(() => {
    const updateWidth = () => {
      const w = window.visualViewport?.width || window.innerWidth || document.documentElement.clientWidth || 375;
      setBarWidth(Math.round(w));
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    window.addEventListener('orientationchange', updateWidth);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateWidth);
    }
    return () => {
      window.removeEventListener('resize', updateWidth);
      window.removeEventListener('orientationchange', updateWidth);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', updateWidth);
      }
    };
  }, []);

  const barHeight = 66;
  const center = barWidth / 2;

  // Edge-to-edge full width dock path (border-radius removed, left/right/bottom gaps removed)
  const floatingDockPathD = useMemo(() => {
    const w = barWidth;
    const h = barHeight + 50; // Extra downward height to cover iPhone safe-area home indicator
    const c = center;
    return `
      M 0,0
      L ${c - 46},0
      C ${c - 34},0 ${c - 30},14 ${c - 22},28
      C ${c - 14},40 ${c - 7},40 ${c},40
      C ${c + 7},40 ${c + 14},40 ${c + 22},28
      C ${c + 30},14 ${c + 34},0 ${c + 46},0
      L ${w},0
      L ${w},${h}
      L 0,${h}
      Z
    `.replace(/\s+/g, ' ').trim();
  }, [barWidth, center, barHeight]);

  // Crisp top contour stroke path (following flat top edge and scoop curve)
  const topContourPathD = useMemo(() => {
    const w = barWidth;
    const c = center;
    return `
      M 0,0
      L ${c - 46},0
      C ${c - 34},0 ${c - 30},14 ${c - 22},28
      C ${c - 14},40 ${c - 7},40 ${c},40
      C ${c + 7},40 ${c + 14},40 ${c + 22},28
      C ${c + 30},14 ${c + 34},0 ${c + 46},0
      L ${w},0
    `.replace(/\s+/g, ' ').trim();
  }, [barWidth, center]);

  // Automatically detect any active modals, drawers, or overlay backdrops in the DOM to hide the dock
  const [hasActiveOverlay, setHasActiveOverlay] = useState(false);

  useEffect(() => {
    const checkOverlay = () => {
      if (typeof document === 'undefined') return;
      const body = document.body;
      const hasClass =
        body.classList.contains('mobile-modal-open') ||
        body.classList.contains('card-drawer-open') ||
        body.classList.contains('app-sec-drawer-open') ||
        body.classList.contains('template-modal-open');

      const hasElement = Boolean(
        document.querySelector(
          '.card-drawer-wrapper, .modal-backdrop, .app-sec-drawer-backdrop, .app-sec-drawer-wrapper, .vault-modal-overlay, .modal-overlay:not(.no-dock-hide), [data-hide-dock="true"]'
        )
      );

      setHasActiveOverlay(hasClass || hasElement);
    };

    checkOverlay();
    const observer = new MutationObserver(checkOverlay);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'data-hide-dock'],
    });

    return () => observer.disconnect();
  }, []);

  const isMessagesPage = location.pathname.startsWith('/messages');
  const hideDock = Boolean(
    isSidebarOpen ||
    isProfileOpen ||
    isSearchOpen ||
    isChatOpen ||
    isModalOpen ||
    hideDockProp ||
    isMessagesPage ||
    hasActiveOverlay
  );

  const navDomContent = (
    <>
      <nav
        ref={containerRef}
        className={`mobile-bottom-dock-wrapper ${hideDock ? 'dock-hidden' : ''}`}
        style={hideDock ? { display: 'none', pointerEvents: 'none', visibility: 'hidden', opacity: 0 } : undefined}
        aria-label="Mobile Navigation"
      >
        {/* Curved Scoop Notch Bar with Solid Opaque Backdrop (No transparency, edge-to-edge) */}
        <div className="mobile-curved-nav-bar">
          {/* SVG Canvas for Solid Opaque Fill and Crisp Top Border */}
          <svg
            className="mobile-nav-backdrop-svg"
            width="100%"
            height={barHeight + 50}
            viewBox={`0 0 ${barWidth} ${barHeight + 50}`}
            preserveAspectRatio="none"
            aria-hidden="true"
            style={{ overflow: 'visible' }}
          >
            {/* 1. Solid Opaque Base Fill */}
            <path d={floatingDockPathD} className="mobile-nav-svg-fill" />
            {/* 2. Top Specular Border Stroke (crisp line following top edge and curve) */}
            <path d={topContourPathD} className="mobile-nav-svg-stroke" fill="none" />
          </svg>

          {/* Navigation Tabs Layer */}
          <div className="mobile-nav-tabs-row">
            {/* 1. HOME BUTTON */}
            <button
              type="button"
              className={`mobile-nav-tab-btn ${isHomeActive ? 'active' : ''}`}
              onClick={() => {
                setIsSearchOpen(false);
                setIsChatOpen(false);
                setIsProfileOpen(false);
                navigate('/dashboard');
              }}
              aria-label="Dashboard Home"
            >
              <div className="tab-icon-wrapper">
                <Home size={24} strokeWidth={isHomeActive ? 2.4 : 1.85} />
              </div>
              {isHomeActive && <span className="tab-active-dot" />}
            </button>

            {/* 2. SEARCH / EXPLORE BUTTON */}
            <button
              type="button"
              className={`mobile-nav-tab-btn ${isSearchActive ? 'active' : ''}`}
              onClick={() => {
                setIsChatOpen(false);
                setIsProfileOpen(false);
                setIsSearchOpen(prev => !prev);
              }}
              aria-label="Search and Explore"
            >
              <div className="tab-icon-wrapper">
                <Search size={24} strokeWidth={isSearchActive ? 2.4 : 1.85} />
              </div>
              {isSearchActive && <span className="tab-active-dot" />}
            </button>

            {/* 3. CENTER ELEVATED PINNED ACTION BUTTON IN CURVED NOTCH (ONLY THIS SHOWS NAME) */}
            <div className="mobile-nav-center-slot">
              <button
                type="button"
                className={`mobile-center-elevated-btn ${isPinFlipping ? 'flipping' : ''}`}
                onClick={() => {
                  setIsSearchOpen(false);
                  setIsChatOpen(false);
                  setIsProfileOpen(false);
                  handleLaunchActivePinned();
                }}
                title={`Open ${currentPinned.name} (Tap dots to switch)`}
                aria-label={`Open ${currentPinned.name}`}
                style={{
                  background: currentPinned.gradient,
                  boxShadow: `0 10px 24px -2px ${currentPinned.color}80, 0 4px 10px rgba(0,0,0,0.15)`
                }}
              >
                <div className="center-btn-content">
                  <PinnedIcon size={22} color="#ffffff" strokeWidth={2.4} />
                  <span className="center-btn-label">{currentPinned.name}</span>
                </div>
              </button>

              {/* 3-Dot Switcher Indicators */}
              <div
                className="center-pin-dots-row"
                onClick={handleSwitchPinned}
                title="Switch pinned app"
              >
                {threePinnedApps.map((app, idx) => (
                  <span
                    key={app.id}
                    className={`pin-mini-dot ${activePinIndex === idx ? 'active' : ''}`}
                    style={{
                      background: activePinIndex === idx ? (app.color || '#3b82f6') : undefined
                    }}
                  />
                ))}
              </div>
            </div>

            {/* 4. CHAT / MESSAGES BUTTON (NO AI BADGE, NO TEXT LABEL) */}
            <button
              type="button"
              className={`mobile-nav-tab-btn ${location.pathname.startsWith('/messages') ? 'active' : ''}`}
              onClick={() => {
                setIsSearchOpen(false);
                setIsProfileOpen(false);
                navigate('/messages');
              }}
              aria-label="Messages and Chat"
            >
              <div className="tab-icon-wrapper">
                <MessageSquare size={24} strokeWidth={location.pathname.startsWith('/messages') ? 2.4 : 1.85} />
              </div>
              {location.pathname.startsWith('/messages') && <span className="tab-active-dot" />}
            </button>

            {/* 5. PROFILE & SETTINGS BUTTON */}
            <button
              type="button"
              className={`mobile-nav-tab-btn ${isProfileActive ? 'active' : ''}`}
              onClick={() => {
                setIsSearchOpen(false);
                setIsChatOpen(false);
                setIsProfileOpen(prev => !prev);
              }}
              aria-label="Profile and Settings"
            >
              <div className="tab-icon-wrapper">
                <img
                  src={`https://ui-avatars.com/api/?name=${user?.email || 'User'}&background=3b82f6&color=fff&bold=true`}
                  alt="Profile"
                  className="tab-profile-avatar"
                />
              </div>
              {isProfileActive && <span className="tab-active-dot" />}
            </button>
          </div>
        </div>
      </nav>

      {/* MODALS */}
      <MobileSearchExploreModal
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
      />

      <MobileChatAssistantModal
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
      />

      <MobileProfileSettingsModal
        isOpen={isProfileOpen}
        onClose={() => setIsProfileOpen(false)}
        onOpenMobileInstall={onOpenMobileInstall}
      />
    </>
  );

  if (typeof document !== 'undefined') {
    return createPortal(navDomContent, document.body);
  }

  return navDomContent;
}
